import React, { useCallback, useEffect, useRef } from 'react';
import PageLoader from '@/src/components/ui/PageLoader';
import { useProfilePosts } from '@/src/hooks/use-fetch-posts';
import { PostItem } from '../../social/components/PostItem';
import { useResponsive } from '@/src/hooks/use-responsive';

type Props = {
  userUniqueLink: string;
};

export const SocialPostsSection = ({ userUniqueLink }: Props) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProfilePosts(userUniqueLink);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const allPages = data?.pages ?? [];
  const items = allPages.flatMap((p) => p.items ?? []);
  const isMobile = useResponsive('down', 'md');

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '300px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loadMore]);

  return (
    <div className="mb-5">
      {isLoading && <PageLoader />}
      {items.map(({ type, post, repost }) =>
        type === 'repost' ? (
          <PostItem
            key={`repost-${post.id}-${repost.createdAt}`}
            post={post}
            repostedBy={repost.by.uniqueLink}
            isMobile={isMobile}
          />
        ) : (
          <PostItem key={post.id} post={post} isMobile={isMobile} />
        )
      )}
    </div>
  );
};
